import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import s from "./W2MoverDataModal.module.css";
import {
  listDesviosNegarCausadores,
  listDesviosNegarPrimarias,
} from "../api";

function MoverDropdown({
  value,
  onChange,
  options = [],
  placeholder = "Selecione...",
  disabled = false,
  autoFocus = false,
}) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const safeOptions = useMemo(
    () =>
      (Array.isArray(options) ? options : [])
        .map((opt) => String(opt ?? "").trim())
        .filter(Boolean),
    [options]
  );

  useEffect(() => {
    if (autoFocus) {
      const id = setTimeout(() => triggerRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [autoFocus]);

  useEffect(() => {
    if (!open) return undefined;

    const onOutside = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setOpenUp(false);
      setMenuStyle(null);
      return undefined;
    }

    const updateDirection = () => {
      const rootEl = rootRef.current;
      if (!rootEl) return;
      const rect = rootEl.getBoundingClientRect();
      const scrollBoundary =
        rootEl.closest(`.${s.content}`) || rootEl.closest(`.${s.modal}`);
      const boundaryRect = scrollBoundary?.getBoundingClientRect?.() || null;
      const desiredMenuHeight = Math.min(Math.max(safeOptions.length * 40, 56), 220) + 8;
      const spaceBelow = boundaryRect
        ? boundaryRect.bottom - rect.bottom
        : window.innerHeight - rect.bottom;
      const spaceAbove = boundaryRect ? rect.top - boundaryRect.top : rect.top;
      const shouldOpenUp = spaceBelow < desiredMenuHeight && spaceAbove > spaceBelow;
      const availableSpace = Math.min(
        220,
        Math.max(140, Math.floor((shouldOpenUp ? spaceAbove : spaceBelow) - 8))
      );
      const top = shouldOpenUp
        ? Math.max(8, Math.round(rect.top - availableSpace - 4))
        : Math.round(rect.bottom + 4);
      setOpenUp(shouldOpenUp);
      setMenuStyle({
        top: `${top}px`,
        left: `${Math.round(rect.left)}px`,
        width: `${Math.round(rect.width)}px`,
        maxHeight: `${availableSpace}px`,
      });
    };

    updateDirection();
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);
    return () => {
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [open, safeOptions.length]);

  return (
    <div
      ref={rootRef}
      className={`${s.selectRoot} ${open ? s.selectOpen : ""} ${
        openUp ? s.selectOpenUp : ""
      } ${disabled ? s.selectDisabled : ""}`.trim()}
    >
      <button
        ref={triggerRef}
        type="button"
        className={s.selectTrigger}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`${s.selectValue} ${value ? "" : s.selectPlaceholder}`.trim()}>
          {value || placeholder}
        </span>
      </button>

      {open && menuStyle
        ? createPortal(
            <div
              ref={menuRef}
              className={`${s.selectMenu} ${s.selectMenuOpen} ${s.selectMenuFloating}`.trim()}
              role="listbox"
              style={menuStyle}
            >
              {safeOptions.map((opt) => {
                const selected = opt === value;
                return (
                  <button
                    key={opt}
                    type="button"
                    className={`${s.selectOption} ${
                      selected ? s.selectOptionSelected : ""
                    }`.trim()}
                    onClick={() => {
                      onChange(opt);
                      setOpen(false);
                    }}
                    aria-selected={selected}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export default function W2MoverDataModal({
  open,
  onClose = () => {},
  onSubmit = () => {},
  title = "Mover data",
  confirmText = "Confirmar",
  cancelText = "Cancelar",
}) {
  const [causador, setCausador] = useState("");
  const [causaPrimaria, setCausaPrimaria] = useState("");
  const [optionsCausador, setOptionsCausador] = useState([]);
  const [optionsCausaPrimaria, setOptionsCausaPrimaria] = useState([]);
  const [loadingCausadores, setLoadingCausadores] = useState(false);
  const [loadingCausaPrimaria, setLoadingCausaPrimaria] = useState(false);
  const [erroCausadores, setErroCausadores] = useState("");
  const [erroCausaPrimaria, setErroCausaPrimaria] = useState("");
  const downOnBackdropRef = useRef(false);

  useEffect(() => {
    let alive = true;
    if (!open) return undefined;

    setCausador("");
    setCausaPrimaria("");
    setOptionsCausador([]);
    setOptionsCausaPrimaria([]);
    setErroCausadores("");
    setErroCausaPrimaria("");
    setLoadingCausadores(true);

    listDesviosNegarCausadores()
      .then((rows) => {
        if (!alive) return;
        const list = Array.from(
          new Set(
            (Array.isArray(rows) ? rows : [])
              .map((r) =>
                typeof r === "string"
                  ? r.trim()
                  : String(r?.causador ?? "").trim()
              )
              .filter(Boolean)
          )
        );
        setOptionsCausador(list);
      })
      .catch((e) => {
        if (!alive) return;
        setOptionsCausador([]);
        setErroCausadores(
          e?.response?.data?.error ||
            e?.message ||
            "Erro ao carregar causadores."
        );
      })
      .finally(() => {
        if (!alive) return;
        setLoadingCausadores(false);
      });

    return () => {
      alive = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    setCausaPrimaria("");
    setOptionsCausaPrimaria([]);
    setErroCausaPrimaria("");

    if (!causador) return undefined;

    let alive = true;
    setLoadingCausaPrimaria(true);

    listDesviosNegarPrimarias(causador)
      .then((rows) => {
        if (!alive) return;
        const list = Array.from(
          new Set(
            (Array.isArray(rows) ? rows : [])
              .map((r) =>
                typeof r === "string"
                  ? r.trim()
                  : String(r?.causa_primaria ?? r ?? "").trim()
              )
              .filter(Boolean)
          )
        );
        setOptionsCausaPrimaria(list);
      })
      .catch((e) => {
        if (!alive) return;
        setOptionsCausaPrimaria([]);
        setErroCausaPrimaria(
          e?.response?.data?.error ||
            e?.message ||
            "Erro ao carregar causas primárias."
        );
      })
      .finally(() => {
        if (!alive) return;
        setLoadingCausaPrimaria(false);
      });

    return () => {
      alive = false;
    };
  }, [causador, open]);

  const canConfirm = !!causador && !!causaPrimaria;

  const handleConfirm = useCallback(() => {
    if (!canConfirm) return;
    onSubmit({
      causador,
      causa_primaria: causaPrimaria,
    });
  }, [canConfirm, causador, causaPrimaria, onSubmit]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") handleConfirm();
    },
    [onClose, handleConfirm]
  );

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div
      className={s.backdrop}
      onMouseDown={(e) => {
        downOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        const startedOnBackdrop = downOnBackdropRef.current;
        downOnBackdropRef.current = false;
        if (startedOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={s.modal}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Mover data"
      >
        <div className={s.header}>
          <h3 className={s.title}>{title}</h3>

          <button
            className={s.close}
            type="button"
            onClick={onClose}
            aria-label="Fechar"
          >
            x
          </button>
        </div>

        <div className={s.content}>
          <div className={s.card}>
            <p className={s.description}>
              Informe o <b>causador</b> e a <b>causa primária</b>.
            </p>

            <label className={s.label} htmlFor="w2-mover-causador">
              Causador <span className={s.req}>*</span>
            </label>

            <MoverDropdown
              value={causador}
              onChange={setCausador}
              options={optionsCausador}
              placeholder={
                loadingCausadores
                  ? "Carregando causadores..."
                  : optionsCausador.length
                  ? "Selecione o causador..."
                  : "Sem causadores cadastrados"
              }
              disabled={loadingCausadores || optionsCausador.length === 0}
              autoFocus
            />

            {!!erroCausadores && (
              <div className={s.hint} style={{ marginTop: 8 }}>
                Falha ao buscar causadores: {erroCausadores}
              </div>
            )}

            <label className={s.label} htmlFor="w2-mover-causa-primaria" style={{ marginTop: 12 }}>
              Causa Primária <span className={s.req}>*</span>
            </label>

            <MoverDropdown
              value={causaPrimaria}
              onChange={setCausaPrimaria}
              options={optionsCausaPrimaria}
              placeholder={
                !causador
                  ? "Selecione o causador primeiro..."
                  : loadingCausaPrimaria
                  ? "Carregando causas primárias..."
                  : optionsCausaPrimaria.length
                  ? "Selecione a causa primária..."
                  : "Sem causas primárias cadastradas"
              }
              disabled={!causador || loadingCausaPrimaria || optionsCausaPrimaria.length === 0}
            />

            {!!erroCausaPrimaria && (
              <div className={s.hint} style={{ marginTop: 8 }}>
                Falha ao buscar causas primárias: {erroCausaPrimaria}
              </div>
            )}
          </div>
        </div>

        <div className={s.actions}>
          <button
            className={`${s.btn} ${s.btnGhost}`}
            type="button"
            onClick={onClose}
          >
            {cancelText}
          </button>

          <button
            className={`${s.btn} ${s.btnPrimary}`}
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            title={
              !canConfirm ? "Selecione o causador e a causa primária" : "Confirmar"
            }
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
